import type { GetInvoiceInformationResponse } from "@nlc/shared-types";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/button";
import {
  ClearableInput,
  FieldError,
  Label,
  TextArea,
  TextField,
} from "@/components/field";
import { Skeleton } from "@/components/skeleton";

import { BillingErrorState } from "../../../components/BillingErrorState";
import { BillingNotice } from "../../components/BillingNotice";
import {
  useInvoiceInformation,
  useUpdateInvoiceInformation,
} from "../hooks/useInvoiceInformation";
import {
  invoiceInformationFormSchema,
  type InvoiceInformationFormValues,
} from "../utils/invoice-information-schema";

type SingleLineField = Exclude<keyof InvoiceInformationFormValues, "notes">;

const SINGLE_LINE_FIELDS: { label: string; name: SingleLineField }[] = [
  { label: "Full name", name: "fullName" },
  { label: "Bill to (email)", name: "billToEmail" },
  { label: "Secondary email", name: "secondaryEmail" },
  { label: "Company name", name: "companyName" },
  { label: "Address", name: "address" },
];

interface InvoiceInformationPanelProps {
  readonly projectId: number;
}

export function InvoiceInformationPanel({
  projectId,
}: InvoiceInformationPanelProps) {
  const { data, isError, isPending, refetch } =
    useInvoiceInformation(projectId);

  return (
    <section className="flex flex-col gap-2xl rounded-xl border border-border-secondary bg-bg-primary p-xl">
      <h3 className="text-lg font-semibold text-text-primary">
        Invoice information
      </h3>

      <BillingNotice
        description="Changes will apply to future invoices only."
        title="Upcoming updates"
      />

      {isPending && <InvoiceInformationSkeleton />}

      {isError && (
        <BillingErrorState
          message="Failed to load invoice information."
          onRetry={() => refetch()}
        />
      )}

      {data && (
        <InvoiceInformationForm
          invoiceInformation={data}
          projectId={projectId}
        />
      )}
    </section>
  );
}

function InvoiceInformationForm({
  invoiceInformation,
  projectId,
}: {
  invoiceInformation: GetInvoiceInformationResponse;
  projectId: number;
}) {
  const updateInvoiceInformation = useUpdateInvoiceInformation(projectId);
  const { control, formState, handleSubmit, reset, setValue } =
    useForm<InvoiceInformationFormValues>({
      defaultValues: invoiceInformation,
      mode: "onChange",
      resolver: zodResolver(invoiceInformationFormSchema),
    });

  const save = (values: InvoiceInformationFormValues) => {
    const changes = Object.fromEntries(
      Object.keys(formState.dirtyFields).map((name) => [
        name,
        values[name as keyof InvoiceInformationFormValues],
      ]),
    );

    updateInvoiceInformation.mutate(changes, {
      onSuccess: () => reset(values),
    });
  };

  return (
    <form className="flex flex-col gap-xl" onSubmit={handleSubmit(save)}>
      {SINGLE_LINE_FIELDS.map(({ label, name }) => (
        <Controller
          control={control}
          key={name}
          name={name}
          render={({
            field: { onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              isInvalid={invalid}
              onBlur={onBlur}
              onChange={onChange}
              validationBehavior="aria"
              value={value}
            >
              <Label>{label}</Label>
              <ClearableInput
                onClear={() =>
                  setValue(name, "", {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                ref={ref}
                showClearButton={!!value}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      ))}

      <Controller
        control={control}
        name="notes"
        render={({ field: { onBlur, onChange, ref, value } }) => (
          <TextField onBlur={onBlur} onChange={onChange} value={value}>
            <Label>Notes / memo</Label>
            <TextArea className="resize-none" ref={ref} rows={4} />
          </TextField>
        )}
      />

      <Button
        className="w-full"
        isDisabled={
          !formState.isDirty ||
          !formState.isValid ||
          updateInvoiceInformation.isPending
        }
        isLoading={updateInvoiceInformation.isPending}
        size="lg"
        type="submit"
      >
        Save changes
      </Button>
    </form>
  );
}

function InvoiceInformationSkeleton() {
  return (
    <div aria-busy="true" className="flex flex-col gap-xl">
      {SINGLE_LINE_FIELDS.map(({ name }) => (
        <div className="flex flex-col gap-sm" key={name}>
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-11 w-full rounded-md" />
        </div>
      ))}
      <Skeleton className="h-24 w-full rounded-md" />
      <Skeleton className="h-11 w-full rounded-md" />
    </div>
  );
}
